export type RuntimeEntityState = {
  matches: Record<string, any>;
  teams: Record<string, any>;
  competitions: Record<string, any>;
  venues: Record<string, any>;
  countries: Record<string, any>;
  stages: Record<string, any>;
  seasons: Record<string, any>;
};

type RuntimePollState = {
  active: boolean;
  lastStartedAt: number;
};

export type RuntimeStoreEvent =
  | "matches:update"
  | "odds:update"
  | "relations:update"
  | "entities:update";

export type RuntimeStorePayload = {
  changedIds?: string[];
  addedIds?: string[];
  relationKeys?: string[];
  source?: string;
};

type RuntimeStoreListener = (payload: RuntimeStorePayload) => void;

export type RuntimeMergeMatchResult = {
  changed: boolean;
  added: boolean;
  match: any;
};

export type RuntimeMergeMatchesResult = {
  ids: string[];
  changedIds: string[];
  addedIds: string[];
};

export const createRuntimeEntityState = (): RuntimeEntityState => ({
  matches: {},
  teams: {},
  competitions: {},
  venues: {},
  countries: {},
  stages: {},
  seasons: {},
});

const IMPORTANT_INFO_KEYS = [
  "statusId",
  "status",
  "matchTime",
  "matchTime2",
  "matchTime3",
  "homeScores",
  "awayScores",
  "homeTeamStats",
  "awayTeamStats",
  "homePosition",
  "awayPosition",
  "homeTeamId",
  "awayTeamId",
  "competitionId",
  "venueId",
  "round",
  "roundNum",
  "seasonId",
  "livestreams",
];

const IMPORTANT_MATCH_KEYS = [
  "info",
  "livestreams",
  "asia",
  "asiaHt",
  "eu",
  "euHt",
  "bs",
  "bsHt",
  "cr",
  "cs",
  "csHt",
  "odds",
  "markets",
  "bookmakers",
];

const preservedMarketKeys = [
  "livestreams",
  "asia",
  "asiaHt",
  "eu",
  "euHt",
  "bs",
  "bsHt",
  "cr",
  "cs",
  "csHt",
  "odds",
  "markets",
  "bookmakers",
  "resultStats",
];

const marketRowCompanyId = (value: any): string =>
  String(value?.companyId ?? value?.company_id ?? value?.cid ?? "");

const mergeMarketSource = (current: any, next: any): any => {
  if (current == null) return next;
  if (next == null) return current;
  if (Array.isArray(current) && Array.isArray(next)) {
    const companyRows = [...current, ...next].every(
      (row) => row && typeof row === "object" && !Array.isArray(row) && marketRowCompanyId(row),
    );
    if (!companyRows) return next;
    const rows = new Map<string, any>();
    current.forEach((row) => rows.set(marketRowCompanyId(row), row));
    next.forEach((row) => {
      const id = marketRowCompanyId(row);
      rows.set(id, { ...(rows.get(id) || {}), ...row });
    });
    return [...rows.values()];
  }
  if (
    typeof current === "object" && !Array.isArray(current) &&
    typeof next === "object" && !Array.isArray(next)
  ) {
    const merged: Record<string, any> = { ...current };
    Object.entries(next).forEach(([key, value]) => {
      merged[key] = key in merged ? mergeMarketSource(merged[key], value) : value;
    });
    return merged;
  }
  return next;
};

const equalRuntimeValue = (left: any, right: any): boolean => {
  if (left === right) return true;
  if (left == null || right == null) return left === right;
  if (typeof left !== typeof right) return false;

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    if (left.length !== right.length) return false;
    return left.every((item, index) => equalRuntimeValue(item, right[index]));
  }

  if (typeof left === "object") {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every((key) => equalRuntimeValue(left[key], right[key]));
  }

  return false;
};

const resultStatPair = (bucket: any, typeId: string): any[] => {
  const value = bucket?.[typeId];
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object")
    return [value.home ?? value[0], value.away ?? value[1]];
  return [];
};

const resultStatsFromBuckets = (stats: any, side: 0 | 1): Record<string, any> => {
  if (!stats || typeof stats !== "object" || Array.isArray(stats)) return {};
  const ftCorner = resultStatPair(stats.ft, "2")[side];
  const h1Corner = resultStatPair(stats.p1, "2")[side];
  const ftYellow = resultStatPair(stats.ft, "3")[side];
  const h1Yellow = resultStatPair(stats.p1, "3")[side];
  const ftRed = resultStatPair(stats.ft, "4")[side];
  const values: Record<string, any> = {};
  if (ftCorner != null) values.corners = ftCorner;
  if (h1Corner != null) values.firstHalfCorners = h1Corner;
  if (ftYellow != null) values.yellowCards = ftYellow;
  if (h1Yellow != null) values.firstHalfYellowCard = h1Yellow;
  if (ftRed != null) values.redCards = ftRed;
  return values;
};

const RELATION_ENTITY_KEYS: (keyof Omit<RuntimeEntityState, "matches">)[] = [
  "teams",
  "competitions",
  "venues",
  "countries",
  "stages",
  "seasons",
];

const importantMatchChanged = (current: any, next: any): boolean => {
  if (!current) return true;
  if (!equalRuntimeValue(current?.id, next?.id)) return true;

  const currentInfo = current?.info || {};
  const nextInfo = next?.info || {};
  for (const key of IMPORTANT_INFO_KEYS) {
    if (!equalRuntimeValue(currentInfo[key], nextInfo[key])) return true;
  }

  for (const key of IMPORTANT_MATCH_KEYS) {
    if (key === "info") continue;
    if (next?.[key] !== undefined && !equalRuntimeValue(current?.[key], next?.[key]))
      return true;
  }

  return false;
};

export const mergeRuntimeMatch = (
  entities: RuntimeEntityState,
  id: string,
  next: any,
): RuntimeMergeMatchResult => {
  const current = entities.matches[id];
  const nextInfo = next?.info || {};
  const nextStats = next?.stats || {};
  const currentResultStats = current?.resultStats || {};
  const nextResultStats = next?.resultStats || {};
  const bucketHomeStats = resultStatsFromBuckets(nextStats, 0);
  const bucketAwayStats = resultStatsFromBuckets(nextStats, 1);
  const nextHomeResultStats =
    nextInfo.homeTeamStats ||
    nextStats.homeTeamStats ||
    nextStats.home ||
    nextStats.homeTeam ||
    bucketHomeStats;
  const nextAwayResultStats =
    nextInfo.awayTeamStats ||
    nextStats.awayTeamStats ||
    nextStats.away ||
    nextStats.awayTeam ||
    bucketAwayStats;
  const merged: any = {
    ...(current || {}),
    ...(next || {}),
    info: {
      ...(current?.info || {}),
      ...nextInfo,
    },
    resultStats: {
      home: {
        ...(currentResultStats.home || {}),
        ...(nextResultStats.home || {}),
        ...nextHomeResultStats,
      },
      away: {
        ...(currentResultStats.away || {}),
        ...(nextResultStats.away || {}),
        ...nextAwayResultStats,
      },
    },
  };

  preservedMarketKeys.forEach((key) => {
    if (next?.[key] === undefined && current?.[key] !== undefined)
      merged[key] = current[key];
    else if (next?.[key] !== undefined && current?.[key] !== undefined)
      merged[key] = mergeMarketSource(current[key], next[key]);
  });

  const added = !current;
  const changed = added || importantMatchChanged(current, merged);
  entities.matches[id] = merged;
  return { added, changed, match: merged };
};

export const mergeRuntimeMatches = (
  entities: RuntimeEntityState,
  matches: any[],
): RuntimeMergeMatchesResult => {
  const ids: string[] = [];
  const changedIds: string[] = [];
  const addedIds: string[] = [];

  matches.forEach((match) => {
    if (!match?.id) return;
    const id = String(match.id);
    const result = mergeRuntimeMatch(entities, id, match);
    ids.push(id);
    if (result.added) addedIds.push(id);
    if (result.changed) changedIds.push(id);
  });

  return { ids, changedIds, addedIds };
};

const normalizeQueryValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map((item) => String(item));
  if (value === undefined || value === null || value === "") return "";
  return String(value);
};

export const runtimeRequestKey = (
  endpoint: string,
  query: Record<string, any>,
): string => {
  const url = new URL(endpoint, location.origin);
  const queryPairs = Object.keys(query)
    .filter((key) => {
      const value = query[key];
      return value !== undefined && value !== null && value !== "";
    })
    .sort()
    .map((key) => [key, normalizeQueryValue(query[key])]);

  return JSON.stringify([url.pathname, queryPairs]);
};

const buildUrl = (endpoint: string, query: Record<string, any>): string => {
  const url = new URL(endpoint, location.origin);

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((item) => url.searchParams.append(key, String(item)));
      return;
    }
    url.searchParams.set(key, String(value));
  });

  return url.toString();
};

const createLivescoreRuntime = () => {
  const entities = createRuntimeEntityState();
  const inFlight = new Map<string, Promise<any>>();
  const polls = new Map<string, RuntimePollState>();
  const listeners = new Map<RuntimeStoreEvent, Set<RuntimeStoreListener>>();
  let batchDepth = 0;
  const pendingEvents = new Map<RuntimeStoreEvent, RuntimeStorePayload>();

  const fetchJson = (
    endpoint: string,
    query: Record<string, any>,
    signal?: AbortSignal,
  ): Promise<any> => {
    if (signal) {
      return fetch(buildUrl(endpoint, query), {
        credentials: "same-origin",
        signal,
      }).then((response) => {
        if (!response.ok)
          throw new Error(`Livescore request failed: ${response.status}`);
        return response.json();
      });
    }

    const key = runtimeRequestKey(endpoint, query);
    const current = inFlight.get(key);
    if (current) return current;

    const promise = fetch(buildUrl(endpoint, query), {
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok)
          throw new Error(`Livescore request failed: ${response.status}`);
        return response.json();
      })
      .finally(() => {
        inFlight.delete(key);
      });

    inFlight.set(key, promise);
    return promise;
  };

  const beginPoll = (key: string, intervalMs: number): boolean => {
    const now = Date.now();
    const current = polls.get(key);
    if (current?.active) return false;
    if (current && now - current.lastStartedAt < intervalMs * 0.8) return false;

    polls.set(key, { active: true, lastStartedAt: now });
    return true;
  };

  const endPoll = (key: string) => {
    const current = polls.get(key);
    if (!current) return;
    current.active = false;
  };

  const mergePayload = (
    current: RuntimeStorePayload = {},
    next: RuntimeStorePayload = {},
  ): RuntimeStorePayload => ({
    ...current,
    ...next,
    changedIds: Array.from(
      new Set([...(current.changedIds || []), ...(next.changedIds || [])]),
    ),
    addedIds: Array.from(
      new Set([...(current.addedIds || []), ...(next.addedIds || [])]),
    ),
    relationKeys: Array.from(
      new Set([...(current.relationKeys || []), ...(next.relationKeys || [])]),
    ),
  });

  const dispatch = (event: RuntimeStoreEvent, payload: RuntimeStorePayload) => {
    listeners.get(event)?.forEach((listener) => listener(payload));
  };

  const flush = () => {
    if (batchDepth > 0 || pendingEvents.size === 0) return;
    const events = Array.from(pendingEvents.entries());
    pendingEvents.clear();
    events.forEach(([event, payload]) => dispatch(event, payload));
  };

  const emit = (event: RuntimeStoreEvent, payload: RuntimeStorePayload = {}) => {
    if (batchDepth > 0) {
      pendingEvents.set(event, mergePayload(pendingEvents.get(event), payload));
      return;
    }
    dispatch(event, payload);
  };

  const batch = (callback: () => void) => {
    batchDepth += 1;
    try {
      callback();
    } finally {
      batchDepth = Math.max(0, batchDepth - 1);
      flush();
    }
  };

  const subscribe = (
    event: RuntimeStoreEvent,
    listener: RuntimeStoreListener,
  ) => {
    const bucket = listeners.get(event) || new Set<RuntimeStoreListener>();
    bucket.add(listener);
    listeners.set(event, bucket);
    return () => {
      bucket.delete(listener);
      if (bucket.size === 0) listeners.delete(event);
    };
  };

  return {
    batch,
    beginPoll,
    emit,
    endPoll,
    entities,
    fetchJson,
    mergeMatch(id: string, match: any) {
      return mergeRuntimeMatch(entities, id, match);
    },
    mergeMatches(matches: any[]) {
      return mergeRuntimeMatches(entities, matches);
    },
    mergeRelations(relation: Record<string, any> = {}) {
      const relationKeys: string[] = [];
      RELATION_ENTITY_KEYS.forEach((key) => {
        const source = relation[key];
        const values = Array.isArray(source)
          ? source
          : source && typeof source === "object"
            ? Object.values(source)
            : [];
        let changed = false;
        values.forEach((value: any) => {
          const id = String(value?.id ?? "");
          if (!id) return;
          const current = entities[key][id];
          const next = { ...(current || {}), ...value };
          if (!equalRuntimeValue(current, next)) changed = true;
          entities[key][id] = next;
        });
        if (changed) relationKeys.push(key);
      });
      return relationKeys;
    },
    requestKey: runtimeRequestKey,
    subscribe,
  };
};

export const livescoreRuntime = createLivescoreRuntime();

declare global {
  interface Window {
    gwdLivescoreState?: typeof livescoreRuntime;
  }
}

if (typeof window !== "undefined") {
  window.gwdLivescoreState = livescoreRuntime;
}
